/**
 * ClarificationModal — Görev 5 FE surface for the backend's material-
 * ambiguity gate.
 *
 * Lifecycle:
 *   1. `SetupWizard.onStart` (or any `useStartDebate().start()` caller)
 *      calls `sessionSlice.start()`, which POSTs `/api/debate`.
 *   2. If the backend responds `409 {status:"needs_clarification"}`,
 *      `sseClient` throws a typed `NeedsClarificationError` that
 *      `sessionSlice.start()` swallows and turns into
 *      `store.clarification = {questions, frame}` (status stays
 *      "draft" — this is NOT an error banner).
 *   3. This modal renders whenever `store.clarification` is non-null,
 *      collects free-text answers, and resubmits via the SAME
 *      `useStartDebate().start()` path with either
 *      `clarificationAnswers` (≥1 filled answer is enough to unblock
 *      the gate server-side) or `skipClarification: true`.
 *
 * Mounted at the page root (`ConsensusModePage`), as a SIBLING of
 * `WizardModal` — NOT nested inside it. The gate can be hit from the
 * header's direct "Tartışmayı Başlat" CTA (`AppHeader.onStart`) when
 * personas are already staged, which starts a debate WITHOUT opening
 * `WizardModal`'s Radix `Dialog.Root`. Radix strips closed dialog
 * content from the tree, so this component previously lived inside
 * `SetupWizard` and silently failed to render on that path — the
 * `clarification` state was set correctly but nothing was listening
 * (2026-07-22). It needs no "is the wizard open" check of its own: it
 * renders purely off `store.clarification`, independent of `wizardOpen`.
 */
import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, useReducedMotion } from "framer-motion";
import { HelpCircle, SkipForward } from "lucide-react";
import { toast } from "sonner";

import { useDebateStore } from "../../store/debateStore";
import { useStartDebate } from "../../hooks/useStartDebate";
import { Button } from "../common/Button";
import { cn } from "../common/cn";

export function ClarificationModal() {
  const clarification = useDebateStore((s) => s.clarification);
  const setClarification = useDebateStore((s) => s.setClarification);
  const debate = useStartDebate();
  const reduceMotion = useReducedMotion();

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<"submit" | "skip" | null>(null);

  // Reset the draft answers every time a FRESH prompt arrives so a
  // resubmission that 409s again (still materially ambiguous) doesn't
  // show the operator's previous answers as if they were unanswered.
  useEffect(() => {
    setAnswers({});
  }, [clarification]);

  if (!clarification || clarification.questions.length === 0) return null;

  const resubmit = async (
    e: React.MouseEvent<HTMLButtonElement>,
    mode: "submit" | "skip",
  ) => {
    const gesture = {
      origin: "click" as const,
      isTrusted: e.nativeEvent.isTrusted,
      timeStamp: e.nativeEvent.timeStamp,
    };
    setBusy(mode);
    try {
      if (mode === "skip") {
        await debate.start(gesture, { skipClarification: true });
      } else {
        const filled = Object.fromEntries(
          Object.entries(answers).filter(([, v]) => v.trim().length > 0),
        );
        await debate.start(gesture, { clarificationAnswers: filled });
      }
      // A successful resubmission that does NOT 409 again clears
      // `clarification` inside `sessionSlice.start()`'s fresh-session
      // reset — nothing else to do here.
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Tartışma başlatılamadı: ${msg}`);
    } finally {
      setBusy(null);
    }
  };

  const hasAnyAnswer = Object.values(answers).some((v) => v.trim().length > 0);

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) setClarification(null);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-[60] bg-[hsl(var(--bg-page)/0.78)] backdrop-blur-md",
            reduceMotion
              ? ""
              : "data-[state=open]:animate-in data-[state=open]:fade-in-0",
          )}
        />
        <Dialog.Content
          aria-describedby={undefined}
          // Yanıtlar yalnızca bu bileşenin yerel state'inde duruyor ve
          // kapanışta `setClarification(null)` ile birlikte yok oluyor.
          // Boşluğa tıklamak yazılmış cevapları sessizce silmemeli: iş
          // sürerken ya da yazılmış bir yanıt varken kazara kapanmayı
          // engelliyoruz. Vazgeçmek isteyen kullanıcı için kapatma
          // düğmesi ve "Yine de devam et" yolu duruyor.
          onInteractOutside={(e) => {
            if (busy || hasAnyAnswer) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (busy) e.preventDefault();
          }}
          className="fixed left-1/2 top-1/2 z-[70] w-[min(94vw,560px)] -translate-x-1/2 -translate-y-1/2 outline-none"
        >
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 12, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="glass relative overflow-hidden rounded-2xl border border-amber-400/25 bg-surface-1/95 shadow-2xl"
          >
            <header className="flex items-start gap-3 border-b border-white/[0.06] px-5 py-4">
              <div
                aria-hidden
                className="grid h-9 w-9 flex-none place-items-center rounded-full border border-amber-400/40 bg-amber-500/15 text-amber-200"
              >
                <HelpCircle className="h-4.5 w-4.5" strokeWidth={1.6} />
              </div>
              <div className="min-w-0 flex-1">
                <Dialog.Title className="text-sm font-semibold text-fg-strong">
                  Devam etmeden önce netleştirelim
                </Dialog.Title>
                <p className="mt-0.5 text-xs leading-relaxed text-fg-subtle">
                  Sorunun eksik kısımları kararı maddi biçimde
                  değiştirebilir. Aşağıdaki sorulardan birine yanıt
                  vererek devam edebilir, ya da bu adımı atlayıp konseyi
                  makul varsayımlarla başlatabilirsin.
                </p>
              </div>
            </header>

            <div className="flex max-h-[50vh] flex-col gap-3 overflow-y-auto px-5 py-4">
              {clarification.questions.map((q, i) => (
                <label key={`${i}-${q}`} className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium text-fg-strong">{q}</span>
                  <input
                    type="text"
                    value={answers[q] ?? ""}
                    onChange={(e) =>
                      setAnswers((prev) => ({ ...prev, [q]: e.target.value }))
                    }
                    placeholder="Yanıtını yaz (opsiyonel — boş bırakılabilir)"
                    maxLength={400}
                    disabled={busy !== null}
                    className="focus-ring rounded-lg border border-white/10 bg-surface-0/60 px-3 py-2 text-fg-strong placeholder:text-fg-subtle"
                  />
                </label>
              ))}
            </div>

            <footer className="flex items-center justify-between gap-2 border-t border-white/[0.06] px-5 py-3.5">
              <Button
                variant="ghost"
                onClick={(e) => void resubmit(e, "skip")}
                disabled={busy !== null}
              >
                <SkipForward className="h-4 w-4" />
                {busy === "skip" ? "Başlatılıyor…" : "Atla ve başlat"}
              </Button>
              <Button
                variant="primary"
                onClick={(e) => void resubmit(e, "submit")}
                disabled={busy !== null || !hasAnyAnswer}
              >
                {busy === "submit" ? "Başlatılıyor…" : "Yanıtla ve başlat"}
              </Button>
            </footer>
          </motion.div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default ClarificationModal;
